import { Check, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Popover as PopoverPrimitive, Select as SelectPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { controlBase } from './input.js';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface SelectProps {
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
  /** Accessible name when not wrapped in a FormField. */
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

/** Single-select dropdown (Radix Select). Option values must be non-empty strings. */
export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(function Select(
  { value, onValueChange, options, placeholder = 'Seleccionar…', disabled, className, id, name, ...aria },
  ref,
) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled} name={name}>
      <SelectPrimitive.Trigger
        ref={ref}
        id={id}
        className={cn(controlBase, 'flex items-center justify-between gap-2 text-left data-[placeholder]:text-text-muted', className)}
        {...aria}
      >
        <span className="truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon>
          <ChevronDown aria-hidden className="size-[18px] shrink-0 text-text-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className="z-50 max-h-[min(320px,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-menu border border-border-subtle bg-surface-default shadow-overlay animate-fade-in"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex cursor-pointer select-none items-center gap-2 rounded-[8px] py-2 pl-8 pr-3 text-body text-text-primary outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:bg-brand-tint data-[disabled]:opacity-50"
              >
                <SelectPrimitive.ItemIndicator className="absolute left-2 inline-flex">
                  <Check aria-hidden className="size-4 text-brand-primary" />
                </SelectPrimitive.ItemIndicator>
                <div className="grid">
                  <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                  {o.description ? <span className="text-helper text-text-secondary">{o.description}</span> : null}
                </div>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});

export interface ComboboxProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Show an option that clears the selection. */
  clearLabel?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/** Searchable single select following the ARIA combobox + listbox pattern. */
export function Combobox({
  value,
  onValueChange,
  options,
  placeholder = 'Seleccionar…',
  searchPlaceholder = 'Buscar…',
  emptyText = 'Sin resultados',
  clearLabel,
  disabled,
  id,
  className,
  ...aria
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const listId = React.useId();
  const selected = options.find((o) => o.value === value);
  const q = query.trim().toLocaleLowerCase('es');
  const filtered = React.useMemo(
    () => (q ? options.filter((o) => o.label.toLocaleLowerCase('es').includes(q)) : options),
    [options, q],
  );
  const items: Array<SelectOption & { clear?: boolean }> = clearLabel ? [{ value: '__clear__', label: clearLabel, clear: true }, ...filtered] : [...filtered];

  const choose = (i: number) => {
    const item = items[i];
    if (!item || item.disabled) return;
    onValueChange(item.clear ? null : item.value);
    setOpen(false);
    setQuery('');
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setActive(0);
        else setQuery('');
      }}
    >
      <PopoverPrimitive.Trigger
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        className={cn(controlBase, 'flex items-center justify-between gap-2 text-left', !selected && 'text-text-muted', className)}
        {...aria}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <ChevronsUpDown aria-hidden className="size-[18px] shrink-0 text-text-muted" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[max(var(--radix-popover-trigger-width),240px)] rounded-menu border border-border-subtle bg-surface-default p-1 shadow-overlay animate-fade-in"
        >
          <input
            autoFocus
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => Math.min(items.length - 1, a + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(0, a - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                choose(active);
              }
            }}
            className={cn(controlBase, 'mb-1 h-9')}
          />
          <ul id={listId} role="listbox" aria-label={aria['aria-label'] ?? placeholder} className="max-h-64 overflow-auto">
            {items.length === 0 ? <li className="px-3 py-2 text-body text-text-secondary">{emptyText}</li> : null}
            {items.map((o, i) => (
              <li
                key={o.value}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={o.clear ? value === null : o.value === value}
                aria-disabled={o.disabled || undefined}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(i)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-[8px] px-3 py-2 text-body text-text-primary',
                  i === active && 'bg-brand-tint',
                  o.disabled && 'cursor-not-allowed opacity-50',
                  o.clear && 'text-text-secondary',
                )}
              >
                <Check aria-hidden className={cn('size-4 shrink-0 text-brand-primary', (o.clear ? value === null : o.value === value) ? 'opacity-100' : 'opacity-0')} />
                <span className="truncate">{o.label}</span>
              </li>
            ))}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
